import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApplicationSelector,
  NavigationProperties,
  NavigationToolbar,
  NavigationTree,
} from '@/components/menu';
import { ConfirmationDialog } from '@/components/Shared/ConfirmationDialog';
import { navigationStudio } from '@/constants/navigationStudio';
import { useProducts } from '@/hooks/useProducts';
import { useNavigationEngine } from '@/navigation/useNavigation';
import type {
  NavigationNode,
  NavigationNodeActionId,
} from '@/types/navigationConfig';
import { resolveNodeLabel } from '@/utils/navigationDisplayUtils';
import { useNavigationPermission } from '@/utils/navigationPermissions';
import {
  collectAllRoutes,
  collectSiblingLabels,
} from '@/utils/navigationTreeUtils';

export function ProductMenuConfigurationPage() {
  const { t } = useTranslation('entity-portal');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const useCompactLayout = isMobile || isTablet;
  const canEdit = useNavigationPermission('MENU_EDIT');

  const [productSearch, setProductSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [isApplicationDrawerOpen, setIsApplicationDrawerOpen] = useState(false);
  const [isPropertiesDrawerOpen, setIsPropertiesDrawerOpen] = useState(false);
  const [pendingGroupDelete, setPendingGroupDelete] = useState<{
    nodeId: string;
    label: string;
    descendantCount: number;
  } | null>(null);

  const { data: products = [], isLoading: isProductsLoading } = useProducts();

  const filteredProducts = useMemo(() => {
    const query = productSearch.trim().toLowerCase();

    if (!query) {
      return products;
    }

    return products.filter((product) => {
      const name = t(product.name, {
        defaultValue: product.name,
      }).toLowerCase();
      return name.includes(query) || product.id.toLowerCase().includes(query);
    });
  }, [productSearch, products, t]);

  useEffect(() => {
    if (!products.length) {
      setSelectedProductId(null);
      return;
    }

    const stillExists = products.some(
      (product) => product.id === selectedProductId,
    );

    if (!selectedProductId || !stillExists) {
      setSelectedProductId(products[0].id);
    }
  }, [products, selectedProductId]);

  const selectedProduct = products.find(
    (product) => product.id === selectedProductId,
  );

  const {
    nodes,
    selectedNodeId,
    selectedNode,
    expandedIds,
    filteredRows,
    validationIssues,
    isLoading: isTreeLoading,
    isSaving,
    setSelectedNodeId,
    updateSelectedNode,
    addGroup,
    addItem,
    moveNode,
    toggleExpanded,
    expandAll,
    collapseAll,
    createRootMenu,
    saveChanges,
    updateNode,
    deleteNode,
    duplicateNode,
  } = useNavigationEngine(selectedProductId ?? undefined);

  const siblingLabels = useMemo(() => {
    if (!selectedNodeId) {
      return [];
    }

    return collectSiblingLabels(nodes, selectedNodeId).map((label) =>
      resolveNodeLabel(label, t),
    );
  }, [nodes, selectedNodeId, t]);

  const allRoutes = useMemo(() => collectAllRoutes(nodes), [nodes]);

  const handleSelectProduct = useCallback((productId: string) => {
    setSelectedProductId(productId);
    setIsApplicationDrawerOpen(false);
  }, []);

  const countDescendants = useCallback((node: NavigationNode): number => {
    return node.children.reduce(
      (count, child) => count + 1 + countDescendants(child),
      0,
    );
  }, []);

  const handleNodeAction = useCallback(
    (actionId: NavigationNodeActionId, node: NavigationNode) => {
      switch (actionId) {
        case 'openProperties':
          setSelectedNodeId(node.id);
          if (useCompactLayout) {
            setIsPropertiesDrawerOpen(true);
          }
          break;
        case 'delete':
          if (node.type === 'group' && node.children.length > 0) {
            setPendingGroupDelete({
              nodeId: node.id,
              label: resolveNodeLabel(node.label, t),
              descendantCount: countDescendants(node),
            });
            break;
          }

          deleteNode(node.id);
          break;
        case 'hide':
          updateNode(node.id, { visibility: 'hidden' });
          break;
        case 'disable':
          updateNode(node.id, { visibility: 'disabled' });
          break;
        case 'duplicate':
          duplicateNode(node.id);
          break;
        case 'rename':
        case 'move':
        case 'assignRoles':
        case 'configureFeatureFlags':
        case 'configureVisibility':
        default:
          setSelectedNodeId(node.id);
          if (useCompactLayout) {
            setIsPropertiesDrawerOpen(true);
          }
          break;
      }
    },
    [
      countDescendants,
      deleteNode,
      duplicateNode,
      setSelectedNodeId,
      t,
      updateNode,
      useCompactLayout,
    ],
  );

  const handleConfirmGroupDelete = useCallback(() => {
    if (!pendingGroupDelete) {
      return;
    }

    deleteNode(pendingGroupDelete.nodeId);
    setPendingGroupDelete(null);
  }, [deleteNode, pendingGroupDelete]);

  const applicationSelector = (
    <ApplicationSelector
      products={filteredProducts}
      selectedProductId={selectedProductId}
      search={productSearch}
      isLoading={isProductsLoading}
      onSearchChange={setProductSearch}
      onSelect={handleSelectProduct}
    />
  );

  const propertiesPanel = (
    <NavigationProperties
      node={selectedNode}
      siblingLabels={siblingLabels}
      allRoutes={allRoutes}
      isLoading={isTreeLoading}
      onChange={updateSelectedNode}
    />
  );

  const productTitle = selectedProduct
    ? t(selectedProduct.name, { defaultValue: selectedProduct.name })
    : t('navigationConfig.defaultProductTitle');

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        bgcolor: navigationStudio.workspaceBackground,
        minHeight: '100%',
      }}
    >
      {useCompactLayout ? (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <IconButton
            aria-label={t('navigationConfig.openApplicationSelector')}
            onClick={() => setIsApplicationDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            aria-label={t('navigationConfig.openPropertiesPanel')}
            onClick={() => setIsPropertiesDrawerOpen(true)}
            disabled={!selectedNode}
          >
            <TuneIcon />
          </IconButton>
        </Stack>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: `${navigationStudio.applicationSelectorWidth}px minmax(0, 1fr)`,
          },
          gap: 2.5,
          alignItems: 'stretch',
        }}
      >
        {!useCompactLayout ? (
          <Box sx={{ minHeight: 720 }}>{applicationSelector}</Box>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: navigationStudio.panelBorderRadius,
            borderColor: navigationStudio.panelBorderColor,
            bgcolor: navigationStudio.panelBackground,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { md: 720 },
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ px: navigationStudio.contentPadding, py: 2.5 }}
          >
            <Box>
              <Breadcrumbs
                separator={
                  <NavigateNextIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled' }}
                  />
                }
                aria-label={t('navigationConfig.breadcrumbAriaLabel')}
                sx={{ mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t('navigationConfig.breadcrumbConfiguration')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('navigationConfig.breadcrumbApplicationNavigation')}
                </Typography>
              </Breadcrumbs>

              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: navigationStudio.productIconBg,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <DescriptionOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: navigationStudio.productIconColor,
                    }}
                  />
                </Box>
                <Typography variant="h4">
                  {t('navigationConfig.pageTitle', { product: productTitle })}
                </Typography>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1.25} flexShrink={0}>
              <Button
                variant="outlined"
                onClick={() => undefined}
                sx={{ borderColor: 'divider' }}
              >
                {t('navigationConfig.previewMenu')}
              </Button>
              {canEdit ? (
                <Button
                  variant="contained"
                  onClick={() => {
                    void saveChanges();
                  }}
                  disabled={isSaving}
                  sx={{ px: 2.5 }}
                >
                  {t('navigationConfig.saveChanges')}
                </Button>
              ) : null}
            </Stack>
          </Stack>

          <Divider sx={{ borderColor: navigationStudio.panelBorderColor }} />

          <Box sx={{ px: navigationStudio.contentPadding, pt: 2.5, flex: 1 }}>
            <NavigationToolbar
              onAddGroup={addGroup}
              onAddItem={addItem}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              isSaving={isSaving}
              showUtilityActions={false}
              showTreeToggles
            />

            {validationIssues.length > 0 ? (
              <Alert severity="warning" sx={{ mb: 2.5 }}>
                {t('navigationConfig.validationSummary', {
                  count: validationIssues.length,
                })}
              </Alert>
            ) : null}

            <NavigationTree
              rows={filteredRows}
              selectedNodeId={selectedNodeId}
              expandedIds={expandedIds}
              isLoading={isTreeLoading}
              isEmpty={!isTreeLoading && nodes.length === 0}
              onSelect={setSelectedNodeId}
              onToggleExpand={toggleExpanded}
              onMove={moveNode}
              onAction={handleNodeAction}
              onCreateRoot={createRootMenu}
            />
          </Box>

          {!useCompactLayout ? propertiesPanel : null}
        </Paper>
      </Box>

      <Drawer
        anchor="left"
        open={isApplicationDrawerOpen}
        onClose={() => setIsApplicationDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box
          sx={{
            width: navigationStudio.applicationSelectorWidth,
            position: 'relative',
          }}
        >
          <IconButton
            aria-label={t('navigationConfig.closeDrawer')}
            onClick={() => setIsApplicationDrawerOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {applicationSelector}
        </Box>
      </Drawer>

      <Drawer
        anchor="bottom"
        open={isPropertiesDrawerOpen}
        onClose={() => setIsPropertiesDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            maxHeight: '85vh',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          },
        }}
      >
        <Box sx={{ position: 'relative', overflow: 'auto' }}>
          <IconButton
            aria-label={t('navigationConfig.closeDrawer')}
            onClick={() => setIsPropertiesDrawerOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {propertiesPanel}
        </Box>
      </Drawer>

      <ConfirmationDialog
        open={Boolean(pendingGroupDelete)}
        title={t('navigationConfig.deleteGroupConfirmationTitle')}
        description={
          pendingGroupDelete
            ? t('navigationConfig.deleteGroupConfirmationDescription', {
                node: pendingGroupDelete.label,
                count: pendingGroupDelete.descendantCount,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        onClose={() => setPendingGroupDelete(null)}
        onConfirm={handleConfirmGroupDelete}
      />
    </Box>
  );
}
